import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { io } from 'socket.io-client'
import { useGameStore } from '../stores/gameStore'
import { formatUserInfoForStorage } from '../utils/userInfo'

const SocketContext = createContext()

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [heartbeatInterval, setHeartbeatInterval] = useState(null)
  const roundTimerRef = useRef(null)
  
  const {
    player,
    setPlayer,
    setPlayerIdentity,
    setGameState,
    setError,
    setLoading,
    setLobbyPlayers,
    setMatchRequestData,
    setCurrentRound,
    updateTimeRemaining,
    setMatchResult,
    setPlayerSoulsHelped,
    setContinueState,
    resetContinueState,
    resetGame,
    resetPlayer,
    saveToLocalStorage,
    clearLocalStorage,
  } = useGameStore()

  // Helper function to refresh user stats
  const refreshUserStats = async () => {
    if (!player.identity) return
    
    // Re-join lobby to get fresh stats
    socket?.emit('join_lobby', {
      recovery_key: player.recoveryKey
    })
  }

  useEffect(() => {
    // Connect to server
    // In production, use window.location.origin. In local dev, use explicit backend URL
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== 'common-ground.local'
    const serverUrl = isProduction ? window.location.origin : (import.meta.env.VITE_SERVER_URL || 'http://common-ground.local:8000')
    const newSocket = io(serverUrl, {
      autoConnect: true,
      transports: ['websocket', 'polling'],
    })

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('Connected to server')
      setIsConnected(true)
      setError(null)
    })

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server')
      setIsConnected(false)
      setError('Connection lost. Attempting to reconnect...')
      
      // Clear any active timer on disconnect
      clearRoundTimer('socket disconnect')
    })

    newSocket.on('connect_error', (error) => {
      console.error('Connection error:', error)
      setIsConnected(false)
      setError('Failed to connect to server')
      
      // Clear any active timer on connection error
      clearRoundTimer('connection error')
    })

    // Game event handlers
    newSocket.on('identity_created', (data) => {
      setPlayerIdentity(data.identity, data.recovery_key, data.souls_helped)
      saveToLocalStorage()
      setLoading(false)  // Clear loading state
      setError(null)
    })

    newSocket.on('identity_recovered', (data) => {
      setPlayerIdentity(data.identity, data.recovery_key, data.souls_helped)
      saveToLocalStorage()
      setLoading(false)  // Clear loading state
      setError(null)
    })

    newSocket.on('lobby_joined', (data) => {
      setGameState('lobby')
      setLobbyPlayers(data.players_in_lobby)
      // Update player stats if provided
      if (data.your_stats) {
        const currentPlayer = player
        const updatedPlayer = {
          ...currentPlayer,
          stats: data.your_stats
        }
        setPlayer(updatedPlayer)
      }
      setError(null)
    })

    newSocket.on('lobby_updated', (data) => {
      setLobbyPlayers(data.players_in_lobby)
    })

    // Match request flow events
    newSocket.on('match_request_sent', (data) => {
      setGameState('match_request_sent')
      setError(null)
      // Store the request data for display
      setMatchRequestData({
        type: 'sent',
        target_identity: data.target_identity,
        message: data.message
      })
    })

    newSocket.on('match_request_received', (data) => {
      setGameState('match_request_received')
      setError(null)
      // Store the request data for confirmation
      setMatchRequestData({
        type: 'received',
        requester_identity: data.requester_identity,
        requester_id: data.requester_id,
        message: data.message
      })
    })

    newSocket.on('match_declined', (data) => {
      setGameState('lobby')
      setError(data.message)
      setMatchRequestData(null)
    })

    // Partnership fallen event
    newSocket.on('partnership_fallen', (data) => {
      console.log('Partnership fallen:', data)
      
      // Stop the round timer immediately
      clearRoundTimer('partnership fallen')
      
      setMatchResult({
        success: false,
        matches: data.matches,
        level_reached: data.level_reached,
        rounds_played: data.rounds_played,
        rounds_completed: data.rounds_completed,
        message: data.message,
        progress_summary: data.progress_summary,
        partner_identity: data.partner_identity,
        partner_user_id: data.partner_user_id,
        partnership_id: data.partnership_id,
        // Add detailed round data for answer comparison
        player1_answers: data.player1_answers || [],
        player2_answers: data.player2_answers || [],
        match_details: data.match_details || [],
        questions: data.questions || [],
        // Add comprehensive game summary
        game_summary: data.game_summary || null
      })
      setGameState('fallen')
    })

    // Try again request flow events
    newSocket.on('try_again_request_sent', (data) => {
      setGameState('try_again_request_sent')
      setError(null)
      setMatchRequestData({
        type: 'try_again_sent',
        partner_identity: data.partner_identity,
        message: data.message
      })
    })

    newSocket.on('try_again_request_received', (data) => {
      setGameState('try_again_request_received')
      setError(null)
      setMatchRequestData({
        type: 'try_again_received',
        requester_identity: data.requester_identity,
        requester_id: data.requester_id,
        message: data.message
      })
    })

    newSocket.on('try_again_declined', (data) => {
      setGameState('lobby')
      setError(data.message)
      setMatchRequestData(null)
    })

    newSocket.on('partner_left_fallen', (data) => {
      console.log('Partner left fallen state:', data)
      setError(`${data.partner_identity} has returned to purgatory. You must also return to purgatory.`)
      setMatchRequestData(null)
      
      // Clear any pending try again state and force return to lobby
      setTimeout(() => {
        resetGame()
        setGameState('lobby')
        setError(null)
      }, 3000) // Show message for 3 seconds then return to lobby
    })

    // Chat request flow events
    newSocket.on('chat_request_sent', (data) => {
      setGameState('chat_request_sent')
      setError(null)
      setMatchRequestData({
        type: 'chat_request_sent',
        partner_identity: data.partner_identity,
        message: data.message
      })
    })

    newSocket.on('chat_request_received', (data) => {
      setGameState('chat_request_received')
      setError(null)
      setMatchRequestData({
        type: 'chat_request_received',
        requester_identity: data.requester_identity,
        requester_id: data.requester_id,
        message: data.message
      })
    })

    newSocket.on('chat_request_declined', (data) => {
      setGameState('fallen')
      setError(data.message)
      setMatchRequestData(null)
    })

    newSocket.on('play_again_request_sent', (data) => {
      setGameState('play_again_request_sent')
      setError(null)
      setMatchRequestData({
        type: 'play_again_sent',
        target_identity: data.target_identity,
        message: data.message
      })
    })

    newSocket.on('play_again_request_received', (data) => {
      setGameState('play_again_request_received')
      setError(null)
      setMatchRequestData({
        type: 'play_again_received',
        requester_identity: data.requester_identity,
        requester_id: data.requester_id,
        message: data.message
      })
    })

    newSocket.on('play_again_declined', (data) => {
      setGameState('victory') // Go back to victory screen
      setError(data.message)
      setMatchRequestData(null)
    })

    newSocket.on('round_started', (data) => {
      console.log('Round started:', data)
      
      // FIRST: Stop any existing timer immediately
      clearRoundTimer('round start')
      
      // Clear any previous match results and continue state when starting new round
      setMatchResult(null)
      setError(null)
      resetContinueState()
      
      setGameState('in_game')
      setCurrentRound({
        questions: data.questions,
        timeRemaining: data.time_limit,
        answers: [],
        opponentIdentity: data.partner_identity, // Server sends partner_identity
        partnershipId: data.partnership_id,
        rounds_completed: data.rounds_completed || 0,
        roundNumber: data.current_round || 1, // Use current_round from backend
      })
      
      // Start countdown timer (this will also clear any existing timer as backup)
      startRoundTimer(data.time_limit)
    })

    newSocket.on('answer_submitted', (data) => {
      // Handle answer submission confirmation
      if (data.error) {
        setError(data.error)
      }
    })

    // Partnership progression events
    newSocket.on('partnership_success', (data) => {
      console.log('Partnership success:', data)
      
      // Stop the round timer immediately
      clearRoundTimer('partnership success')
      
      setMatchResult({
        success: true,
        matches: data.matches,
        total_matches: data.total_matches,
        rounds_completed: data.rounds_completed,
        escaped: data.escaped || false,
        message: data.message,
        // Add detailed round data for answer comparison
        player1_answers: data.player1_answers || [],
        player2_answers: data.player2_answers || [],
        match_details: data.match_details || [],
        new_level: data.rounds_completed,  // Keep for backward compatibility
        // Add comprehensive game summary
        game_summary: data.game_summary
      })
      
      if (data.escaped) {
        setGameState('victory')
        // Update souls helped count if provided
        if (data.souls_helped) {
          setPlayerSoulsHelped(data.souls_helped)
          saveToLocalStorage()
        }
        // Don't auto-redirect - let user choose "Play Again" or "Return to Purgatory"
      } else {
        setGameState('success')
        // Don't auto-return to lobby - wait for next round to start automatically
        // Server will send 'round_started' again after 3 seconds
      }
    })


    newSocket.on('partnership_escaped', (data) => {
      console.log('Partnership escaped!', data)
      setMatchResult({
        success: true,
        matches: data.matches || 0,
        level: data.level,
        escaped: true,
        message: data.message,
        // Add detailed round data for answer comparison display
        questions: data.questions || [],
        player1_answers: data.player1_answers || [],
        player2_answers: data.player2_answers || [],
        match_details: data.match_details || [],
        // Add partner information for play again functionality
        partner_user_id: data.partner_user_id,
        partner_identity: data.partner_identity,
        // Add comprehensive game summary
        game_summary: data.game_summary
      })
      
      // Update souls helped count
      if (data.souls_helped) {
        setPlayerSoulsHelped(data.souls_helped)
        saveToLocalStorage()
      }
      
      // Refresh user stats after escape
      setTimeout(() => refreshUserStats(), 1000)
      
      setGameState('victory')
      
      // Don't auto-redirect - let user choose "Play Again" or "Return to Purgatory"
    })

    // Legacy round_ended handler (remove after full migration)
    newSocket.on('round_ended', (data) => {
      setMatchResult(data)
      
      if (data.success) {
        // Check if escaped (reached level 10 in this partnership)
        if (data.escaped) {
          setPlayerSoulsHelped(data.souls_helped)  // Server should send updated count
          saveToLocalStorage()
          setGameState('victory')
        } else {
          setGameState('success')
        }
      } else {
        setGameState('failure')
      }
      
      // Auto-return to lobby after showing results
      setTimeout(() => {
        resetGame()
        setGameState('lobby')
      }, 5000)
    })

    newSocket.on('chat_unlocked', (data) => {
      setGameState('chat')
      // Navigate to chat page will be handled by the component
    })

    newSocket.on('chat_created', (data) => {
      console.log('Chat created:', data)
      setGameState('chat')
      // Store chat info for navigation
      setMatchRequestData({
        chat_id: data.chat_id,
        partner_identity: data.partner_identity
      })
    })

    newSocket.on('opponent_disconnected', (data) => {
      setError(data.message)
      
      // Clear timer when opponent disconnects
      clearRoundTimer('opponent disconnect')
      
      resetGame()
      setGameState('lobby')
    })

    // Continue flow events
    newSocket.on('continue_waiting', (data) => {
      console.log('Continue waiting:', data)
      setContinueState({
        userClicked: true,
        partnerWaiting: false,
        waitingForPartner: true,
      })
    })

    newSocket.on('partner_waiting', (data) => {
      console.log('Partner waiting:', data)
      setContinueState({
        userClicked: false,
        partnerWaiting: true,
        waitingForPartner: false,
      })
    })

    newSocket.on('error', (data) => {
      console.log('Server error:', data.message)
      
      // Clear timer on any server error
      clearRoundTimer('server error')
      
      // Only clear identity data if it's a permanent identity error, not session issues
      if (data.message && (data.message.includes('Invalid recovery key') || data.message.includes('User not found'))) {
        console.log('Clearing invalid identity data')
        resetPlayer()
        clearLocalStorage()
        setGameState('idle')
      } else if (data.message && data.message.includes('session may have expired')) {
        console.log('Session expired - will attempt recovery on next action')
        // The next action (like joinLobby) will include recovery key for session restoration
      } else if (data.message && data.message.includes('No identity found')) {
        console.log('Identity issue detected - check if recovery key helps')
        // Don't immediately clear identity - let recovery mechanisms try first
      }
      
      setError(data.message)
      setLoading(false)  // Clear loading state on any error
    })

    setSocket(newSocket)

    // Cleanup on unmount
    return () => {
      stopHeartbeat()
      clearRoundTimer('component unmount')
      newSocket.close()
    }
  }, [])

  // Heartbeat functionality to keep user active
  const startHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
    }
    
    const interval = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('heartbeat', { timestamp: Date.now() })
      }
    }, 30000) // Send heartbeat every 30 seconds
    
    setHeartbeatInterval(interval)
  }

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      setHeartbeatInterval(null)
    }
  }

  // Start heartbeat when connected, stop when disconnected
  useEffect(() => {
    if (isConnected && socket) {
      startHeartbeat()
    } else {
      stopHeartbeat()
    }
    
    return () => stopHeartbeat()
  }, [isConnected, socket])

  // Helper function to clear round timer
  const clearRoundTimer = (reason = 'unknown') => {
    console.log(`Clearing round timer due to: ${reason}, current timer ID: ${roundTimerRef.current}`)
    if (roundTimerRef.current) {
      clearInterval(roundTimerRef.current)
      roundTimerRef.current = null
    }
  }

  // Helper function to start round timer
  const startRoundTimer = (duration) => {
    console.log('Starting round timer with duration:', duration)
    
    // Store previous timer for logging
    const previousTimer = roundTimerRef.current
    
    // Clear any existing timer first using ref
    if (roundTimerRef.current) {
      console.log(`Force clearing existing timer ID: ${roundTimerRef.current}`)
      clearInterval(roundTimerRef.current)
    }
    
    let timeLeft = duration
    updateTimeRemaining(timeLeft) // Set initial time immediately
    
    const timer = setInterval(() => {
      timeLeft -= 1
      updateTimeRemaining(timeLeft)
      
      if (timeLeft <= 0) {
        console.log(`Timer ${timer} reached 0, clearing interval`)
        clearInterval(timer)
        roundTimerRef.current = null
      }
    }, 1000)
    
    roundTimerRef.current = timer
    console.log(`Timer started with interval ID: ${timer}, replacing: ${previousTimer}`)
  }

  // Socket API methods
  const createIdentity = () => {
    if (socket) {
      const userInfo = formatUserInfoForStorage()
      socket.emit('create_identity', { user_info: userInfo })
    }
  }

  const recoverIdentity = (recoveryKey) => {
    if (socket) {
      const userInfo = formatUserInfoForStorage()
      socket.emit('recover_identity', { 
        recovery_key: recoveryKey,
        user_info: userInfo
      })
    }
  }

  const joinLobby = () => {
    if (socket) {
      socket.emit('join_lobby', {
        recovery_key: player.recoveryKey  // Include recovery key for session recovery
      })
    }
  }

  const requestMatch = (targetUserId) => {
    if (socket) {
      socket.emit('request_match', { 
        target_user_id: targetUserId,
        recovery_key: player.recoveryKey  // Include recovery key for session recovery
      })
    }
  }

  const respondToMatch = (requesterId, accepted) => {
    if (socket) {
      socket.emit('respond_to_match', { 
        requester_id: requesterId, 
        accepted: accepted 
      })
    }
  }

  const requestTryAgain = (partnerUserId) => {
    if (socket) {
      socket.emit('request_try_again', { partner_user_id: partnerUserId })
    }
  }

  const respondToTryAgain = (requesterId, accepted) => {
    if (socket) {
      socket.emit('respond_to_try_again', { 
        requester_id: requesterId, 
        accepted: accepted 
      })
    }
  }

  const requestPlayAgain = (partnerUserId) => {
    if (socket) {
      socket.emit('request_play_again', { partner_user_id: partnerUserId })
    }
  }

  const respondToPlayAgain = (requesterId, accepted) => {
    if (socket) {
      socket.emit('respond_to_play_again', { 
        requester_id: requesterId, 
        accepted: accepted 
      })
    }
  }

  const submitAnswer = (questionId, answer) => {
    if (socket) {
      socket.emit('submit_answer', {
        question_id: questionId,
        answer: answer,
      })
    }
  }

  const sendChatMessage = (chatId, message) => {
    if (socket) {
      socket.emit('send_chat_message', {
        chat_id: chatId,
        message: message,
      })
    }
  }

  const continueToNextRound = (partnershipId) => {
    if (socket) {
      socket.emit('continue_to_next_round', {
        partnership_id: partnershipId,
        recovery_key: player.recoveryKey, // Send recovery key for session restoration
      })
    }
  }

  const leaveLobby = () => {
    if (socket) {
      socket.emit('leave_lobby', {})
    }
  }

  const notifyPartnerLeft = (partnerId) => {
    if (socket && partnerId) {
      socket.emit('partner_left_fallen', { 
        partner_id: partnerId
      })
    }
  }

  const requestChat = (partnerId) => {
    if (socket && partnerId) {
      socket.emit('request_chat', { 
        partner_id: partnerId
      })
    }
  }

  const respondToChatRequest = (requesterId, accepted) => {
    if (socket) {
      socket.emit('respond_to_chat_request', { 
        requester_id: requesterId, 
        accepted: accepted 
      })
    }
  }

  const startChat = (partnerId) => {
    if (socket && partnerId) {
      socket.emit('start_chat', { 
        partner_id: partnerId
      })
    }
  }

  const value = {
    socket,
    isConnected,
    createIdentity,
    recoverIdentity,
    joinLobby,
    requestMatch,
    respondToMatch,
    requestTryAgain,
    respondToTryAgain,
    requestPlayAgain,
    respondToPlayAgain,
    requestChat,
    respondToChatRequest,
    notifyPartnerLeft,
    startChat,
    submitAnswer,
    sendChatMessage,
    continueToNextRound,
    leaveLobby,
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}
